import {
  markdownToPreview,
  markdownToPreviewInline,
} from '@components/designer/utils/markdownToPreview';
import type { KnowledgeArticleDTO } from '@conversed/rest/src/contracts/article-architect/article-architect.dto';
import type { KnowledgeArticleStatus } from '@conversed/rest/src/contracts/article-architect/article-architect.enums';
import { purify } from '@helpers/domHelper';
import { isAxiosError } from 'axios';
import parse from 'html-react-parser';

export const parseArticleMarkdown = (body: string): ReturnType<typeof parse> =>
  parse(purify(markdownToPreview(body), 'markdown-link-inherit'));

// For short snippets rendered inside an existing paragraph - the conflict judge's
// evidence quotes. The block renderer would emit a <p>, which cannot legally nest
// inside the quote's own <p>.
//
// The <span> wrapper is load-bearing: `purify` runs DOMPurify with FORCE_BODY: false,
// which silently drops a leading bare text node ("valid for **24h**" would render as
// just "24h"). Block-rendered markdown always starts with a tag, so this never
// surfaced there. Wrapping gives the sanitiser an element to start from.
export const parseArticleMarkdownInline = (
  text: string,
): ReturnType<typeof parse> =>
  parse(
    purify(
      `<span>${markdownToPreviewInline(text)}</span>`,
      'markdown-link-inherit',
    ),
  );

export const filterArticlesByStatus = (
  articles: KnowledgeArticleDTO[],
  status: KnowledgeArticleStatus,
): KnowledgeArticleDTO[] =>
  articles.filter((article) => article.status === status);

// * Every distinct category in A-Z order. The pills do not show how many
// * articles each category has, so an order by article count would look random
// * to the user; A-Z is an order they can follow. The comparison ignores case
// * and accents ("d'Arbois" sorts with the Ds).
export const collectCategoriesAlphabetically = (
  articles: KnowledgeArticleDTO[],
): string[] => {
  const categories = new Set<string>();

  for (const article of articles) {
    if (article.category) {
      categories.add(article.category);
    }
  }

  return [...categories].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' }),
  );
};

// * The categories a collapsed pill row shows. A selected category that falls
// * past the visible ones is added after them (the fitting count already kept
// * room for it), so picking one from the expanded list and collapsing again
// * never hides the active filter.
export const getVisibleCategories = (
  categories: string[],
  visibleCount: number,
  selectedCategory: string | null,
): string[] => {
  const visibleCategories = categories.slice(0, visibleCount);

  if (
    selectedCategory === null ||
    visibleCategories.includes(selectedCategory) ||
    !categories.includes(selectedCategory)
  ) {
    return visibleCategories;
  }

  return [...visibleCategories, selectedCategory];
};

// Counts conflict pairs the way the conflict resolution page does: a pair is
// recorded on both articles, so (A,X) and (X,A) count once, and a conflict with an
// article that no longer exists is not shown there, so it is not counted here.
export const countUniqueConflictPairs = (
  articles: KnowledgeArticleDTO[],
): number => {
  const articleIds = new Set(articles.map((article) => article._id.toString()));
  const seenPairs = new Set<string>();

  for (const article of articles) {
    const articleId = article._id.toString();
    for (const conflict of article.conflicts ?? []) {
      if (!articleIds.has(conflict.articleId)) {
        continue;
      }
      seenPairs.add([articleId, conflict.articleId].sort().join('-'));
    }
  }

  return seenPairs.size;
};

export enum PublishRefusal {
  // * The plain guard: the article already had conflicts, nothing was saved.
  NothingSaved = 'nothing-saved',
  // * The check on a publish that changed the content or the category found
  // * conflicts. The edits are saved, only the status stayed as it was.
  EditsSaved = 'edits-saved',
  // * The check could not run (Bedrock error). The edits are saved, the article
  // * gained no conflicts and can be published again later.
  CheckFailed = 'check-failed',
}

// * Reads the backend's flag rather than the article's own `conflicts`, which
// * can predate a background check and say the article had none.
export const getPublishRefusal = (
  requestError: unknown,
): PublishRefusal | null => {
  if (!isAxiosError(requestError)) {
    return null;
  }
  const errorCode = requestError.response?.data?.errorCode;
  if (errorCode === 'ARTICLE_ARCHITECT_CONFLICT_CHECK_FAILED') {
    return PublishRefusal.CheckFailed;
  }
  if (errorCode !== 'ARTICLE_ARCHITECT_UNRESOLVED_CONFLICTS') {
    return null;
  }
  if (requestError.response?.data?.data?.editsSaved === true) {
    return PublishRefusal.EditsSaved;
  }
  return PublishRefusal.NothingSaved;
};
